import React from 'react';
import { clsx } from 'clsx';
import { Stepper, StepNavigation, StepProgressBar, useStepper, Step } from './Stepper';

// Wizard step content
export interface WizardStepContentProps {
  children: React.ReactNode;
  className?: string;
}

export function WizardStepContent({
  children,
  className,
}: WizardStepContentProps) {
  return (
    <div className={clsx('py-6', className)}>
      {children}
    </div>
  );
}

// Wizard step with validation
export interface WizardStep extends Step {
  content: React.ReactNode;
  validate?: () => boolean | Promise<boolean>;
  onEnter?: () => void;
  onLeave?: () => void;
}

// Wizard context
export interface WizardContextValue {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isValidating: boolean;
  stepData: Record<string, unknown>;
  setStepData: (key: string, value: unknown) => void;
}

const WizardContext = React.createContext<WizardContextValue | null>(null);

export function useWizard(): WizardContextValue {
  const context = React.useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardForm');
  }
  return context;
}

// Wizard form container
export interface WizardFormProps {
  steps: WizardStep[];
  onComplete?: (data: Record<string, unknown>) => void;
  onStepChange?: (step: number) => void;
  initialStep?: number;
  showStepper?: boolean;
  stepperPosition?: 'top' | 'left';
  stepperVariant?: 'default' | 'simple' | 'dots' | 'numbered';
  showProgressBar?: boolean;
  showNavigation?: boolean;
  navigationPosition?: 'top' | 'bottom' | 'both';
  validateOnNext?: boolean;
  className?: string;
}

export function WizardForm({
  steps,
  onComplete,
  onStepChange,
  initialStep = 0,
  showStepper = true,
  stepperPosition = 'top',
  stepperVariant = 'default',
  showProgressBar = false,
  showNavigation = true,
  navigationPosition = 'bottom',
  validateOnNext = true,
  className,
}: WizardFormProps) {
  const [isValidating, setIsValidating] = React.useState(false);
  const [stepData, setStepDataState] = React.useState<Record<string, unknown>>({});
  const [completedSteps, setCompletedSteps] = React.useState<number[]>([]);

  const stepper = useStepper({
    steps,
    initialStep,
    onStepChange,
    onComplete: () => onComplete?.(stepData),
  });

  const setStepData = React.useCallback((key: string, value: unknown) => {
    setStepDataState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleNext = React.useCallback(async () => {
    const currentStepConfig = steps[stepper.currentStep];

    if (validateOnNext && currentStepConfig.validate) {
      setIsValidating(true);
      try {
        const isValid = await currentStepConfig.validate();
        if (!isValid) {
          setIsValidating(false);
          return;
        }
      } catch {
        setIsValidating(false);
        return;
      }
      setIsValidating(false);
    }

    // Mark current step as completed
    if (!completedSteps.includes(stepper.currentStep)) {
      setCompletedSteps((prev) => [...prev, stepper.currentStep]);
    }

    // Call onLeave for current step
    currentStepConfig.onLeave?.();

    // Navigate to next step
    stepper.nextStep();

    // Call onEnter for next step
    if (!stepper.isLastStep) {
      steps[stepper.currentStep + 1]?.onEnter?.();
    }
  }, [steps, stepper, validateOnNext, completedSteps]);

  const handlePrevious = React.useCallback(() => {
    const currentStepConfig = steps[stepper.currentStep];
    currentStepConfig.onLeave?.();
    stepper.previousStep();
    steps[stepper.currentStep - 1]?.onEnter?.();
  }, [steps, stepper]);

  const handleStepClick = React.useCallback((index: number) => {
    if (completedSteps.includes(index) || index < stepper.currentStep) {
      steps[stepper.currentStep].onLeave?.();
      stepper.goToStep(index);
      steps[index].onEnter?.();
    }
  }, [steps, stepper, completedSteps]);

  const contextValue: WizardContextValue = {
    currentStep: stepper.currentStep,
    totalSteps: steps.length,
    isFirstStep: stepper.isFirstStep,
    isLastStep: stepper.isLastStep,
    goToStep: handleStepClick,
    nextStep: handleNext,
    previousStep: handlePrevious,
    canGoNext: stepper.canGoNext,
    canGoPrevious: stepper.canGoPrevious,
    isValidating,
    stepData,
    setStepData,
  };

  const currentStepContent = steps[stepper.currentStep]?.content;

  const stepperElement = showStepper && (
    <Stepper
      steps={steps}
      currentStep={stepper.currentStep}
      orientation={stepperPosition === 'left' ? 'vertical' : 'horizontal'}
      variant={stepperVariant}
      onStepClick={handleStepClick}
      allowClickCompleted
      completedSteps={completedSteps}
    />
  );

  const progressBarElement = showProgressBar && (
    <StepProgressBar
      currentStep={stepper.currentStep}
      totalSteps={steps.length}
    />
  );

  const navigationElement = showNavigation && (
    <StepNavigation
      currentStep={stepper.currentStep}
      totalSteps={steps.length}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onComplete={handleNext}
      loading={isValidating}
    />
  );

  return (
    <WizardContext.Provider value={contextValue}>
      <div
        className={clsx(
          stepperPosition === 'left' && 'flex gap-8',
          className
        )}
      >
        {stepperPosition === 'left' && (
          <div className="flex-shrink-0 w-48">{stepperElement}</div>
        )}
        <div className="flex-1">
          {stepperPosition === 'top' && (
            <div className="mb-6">{stepperElement}</div>
          )}
          {showProgressBar && <div className="mb-6">{progressBarElement}</div>}
          {navigationPosition === 'top' && <div className="mb-6">{navigationElement}</div>}
          {navigationPosition === 'both' && <div className="mb-6">{navigationElement}</div>}

          <WizardStepContent>{currentStepContent}</WizardStepContent>

          {(navigationPosition === 'bottom' || navigationPosition === 'both') && (
            <div className="mt-6">{navigationElement}</div>
          )}
        </div>
      </div>
    </WizardContext.Provider>
  );
}

// Simple wizard card
export interface WizardCardProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function WizardCard({
  title,
  description,
  children,
  footer,
  className,
}: WizardCardProps) {
  return (
    <div className={clsx('bg-white rounded-lg shadow-sm border border-gray-200 dark:bg-gray-800 dark:border-gray-700', className)}>
      {(title || description) && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          {title && <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>}
          {description && <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">{description}</p>}
        </div>
      )}
      <div className="px-6 py-6">{children}</div>
      {footer && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg dark:bg-gray-900 dark:border-gray-700">
          {footer}
        </div>
      )}
    </div>
  );
}

// Wizard page layout
export interface WizardPageProps {
  title: string;
  description?: string;
  illustration?: React.ReactNode;
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  className?: string;
}

export function WizardPage({
  title,
  description,
  illustration,
  children,
  sidebar,
  className,
}: WizardPageProps) {
  return (
    <div className={clsx('min-h-screen bg-gray-50 dark:bg-gray-900', className)}>
      <div className="max-w-4xl mx-auto py-12 px-4">
        <div className="text-center mb-8">
          {illustration && <div className="mb-6">{illustration}</div>}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          {description && (
            <p className="text-gray-500 mt-2 max-w-xl mx-auto dark:text-gray-400">{description}</p>
          )}
        </div>
        <div className={clsx(sidebar && 'flex gap-8')}>
          <div className="flex-1">{children}</div>
          {sidebar && <div className="w-64 flex-shrink-0">{sidebar}</div>}
        </div>
      </div>
    </div>
  );
}

// Step summary for review step
export interface StepSummaryItemProps {
  label: string;
  value: React.ReactNode;
  onEdit?: () => void;
  className?: string;
}

export function StepSummaryItem({
  label,
  value,
  onEdit,
  className,
}: StepSummaryItemProps) {
  return (
    <div className={clsx('flex items-start justify-between py-3 border-b border-gray-100 dark:border-gray-800', className)}>
      <div>
        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          Edit
        </button>
      )}
    </div>
  );
}

export interface StepSummaryProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function StepSummary({
  title,
  children,
  className,
}: StepSummaryProps) {
  return (
    <div className={clsx('bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700', className)}>
      {title && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        </div>
      )}
      <dl className="px-4">{children}</dl>
    </div>
  );
}

// Wizard actions bar
export interface WizardActionsProps {
  onPrevious?: () => void;
  onNext?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  previousLabel?: string;
  nextLabel?: string;
  cancelLabel?: string;
  saveLabel?: string;
  showPrevious?: boolean;
  showNext?: boolean;
  showCancel?: boolean;
  showSave?: boolean;
  disableNext?: boolean;
  loading?: boolean;
  className?: string;
}

export function WizardActions({
  onPrevious,
  onNext,
  onCancel,
  onSave,
  previousLabel = 'Back',
  nextLabel = 'Continue',
  cancelLabel = 'Cancel',
  saveLabel = 'Save',
  showPrevious = true,
  showNext = true,
  showCancel = false,
  showSave = false,
  disableNext = false,
  loading = false,
  className,
}: WizardActionsProps) {
  return (
    <div className={clsx('flex items-center justify-between', className)}>
      <div className="flex items-center gap-3">
        {showCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors dark:text-gray-200"
          >
            {cancelLabel}
          </button>
        )}
        {showPrevious && (
          <button
            type="button"
            onClick={onPrevious}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
          >
            {previousLabel}
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        {showSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
          >
            {saveLabel}
          </button>
        )}
        {showNext && (
          <button
            type="button"
            onClick={onNext}
            disabled={disableNext || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// Wizard help panel
export interface WizardHelpProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function WizardHelp({
  title = 'Need help?',
  children,
  className,
}: WizardHelpProps) {
  return (
    <div className={clsx('bg-blue-50 rounded-lg p-4', className)}>
      <h4 className="text-sm font-medium text-blue-900 mb-2">{title}</h4>
      <div className="text-sm text-blue-700">{children}</div>
    </div>
  );
}

// Wizard tip
export interface WizardTipProps {
  icon?: React.ReactNode;
  children: React.ReactNode;
  variant?: 'info' | 'warning' | 'success';
  className?: string;
}

export function WizardTip({
  icon,
  children,
  variant = 'info',
  className,
}: WizardTipProps) {
  const variantClasses = {
    info: 'bg-blue-50 text-blue-700 border-blue-200',
    warning: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    success: 'bg-green-50 text-green-700 border-green-200',
  };

  const defaultIcons = {
    info: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <div className={clsx('flex gap-3 p-3 rounded-lg border', variantClasses[variant], className)}>
      <div className="flex-shrink-0">{icon || defaultIcons[variant]}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
